import { useCallback, useState, type PropsWithChildren } from "react";

import { type Floor, type FullAirHandlerUnit } from "@eisox/api/core-api";
import { createContext } from "@radix-ui/react-context";

import { airHandlerUnitApi } from "~/api";
import { Mode } from "~/components/live-history-toggle";
import { type HistoryDateChangeOptions } from "~/helpers/history-search.helper";
import { isLiveSampleStale, REALTIME_FROZEN_FETCH_COUNT } from "~/helpers/history.helper";
import { useLiveDataFrozen } from "~/hooks/use-live-data-frozen";
import { useStateHistoryDates } from "~/hooks/use-state-history-dates";
import { useStateSnapshots } from "~/hooks/use-state-snapshots";
import { useStickyRealtimeStale } from "~/hooks/use-sticky-realtime-stale";
import { useBuildingContext } from "~/providers/building.provider";
import type { AirHandlerUnitError } from "~/types/air-handler-unit.type";

import { getErrors, getFloors, getFullAirHandlerUnitsByGroup } from "../helpers";

export type { HistoryDateChangeOptions };

const NAME = "AirHandlerUnitProvider";

interface AirHandlerUnitProviderProps extends PropsWithChildren {
  airHandlerUnits: FullAirHandlerUnit[];
  airHandlerUnitId: string;
  mode: Mode;
  historyDate?: string;
  onModeChange: (mode: Mode) => void;
  onDateChange: (date: string, options?: HistoryDateChangeOptions) => void;
}

interface AirHandlerUnitContextValue {
  name: string;
  isGroup: boolean;
  airHandlerUnit: FullAirHandlerUnit[];
  airHandlerUnits: FullAirHandlerUnit[];
  floors: Floor[];
  errors: AirHandlerUnitError[];
  mode: Mode;
  onModeChange: (mode: Mode) => void;
  isSnapshotLoading: boolean;
  isHistoryDatesLoading: boolean;
  /** Live mode with a stale or frozen feed — forces the realtime-unavailable dialog. */
  isRealtimeStale: boolean;
  liveSampleDate?: Date;
  /** Selected instant (history) or freshest sample (live). */
  date: Date;
  days: string[];
  dates: string[];
  onDateChange: (date: string, options?: HistoryDateChangeOptions) => void;
  onReload: () => unknown;
}

const [AirHandlerUnitContextProvider, useAirHandlerUnitContext] = createContext<AirHandlerUnitContextValue>(NAME);

const AirHandlerUnitProvider: React.FC<AirHandlerUnitProviderProps> = ({
  airHandlerUnits,
  airHandlerUnitId,
  mode,
  historyDate: historyDateFromSearch,
  onModeChange,
  onDateChange,
  children,
}) => {
  const { floors } = useBuildingContext(NAME);

  const [now] = useState(() => new Date());
  const isHistory = mode === Mode.History;

  const airHandlerUnitsByGroup = getFullAirHandlerUnitsByGroup(airHandlerUnits);

  const currentAirHandlerUnit = Object.values(airHandlerUnitsByGroup).find(ahu =>
    ahu.map(ahu => ahu.id).includes(airHandlerUnitId),
  );
  if (!currentAirHandlerUnit) throw new Error(`Air handler unit with id ${airHandlerUnitId} not found`);

  const groupIds = currentAirHandlerUnit.map(ahu => ahu.id);

  const replaceHistoryDate = useCallback((date: string) => onDateChange(date, { replace: true }), [onDateChange]);
  const {
    days: historyDays,
    dates: historyDates,
    isLoading: isHistoryDatesLoading,
  } = useStateHistoryDates({
    enabled: isHistory,
    date: historyDateFromSearch,
    now,
    queryOptions: (startDate, endDate) => airHandlerUnitApi.queries.stateDates(airHandlerUnitId, startDate, endDate),
    onDateChange: replaceHistoryDate,
  });
  const effectiveHistoryDate = isHistory ? historyDateFromSearch : undefined;
  const snapshotHistoryDate = effectiveHistoryDate ?? now.toISOString();

  // Poll every unit's `/state`, not just the viewed group, so aggregated errors stay live.
  const {
    stateQueries,
    merged: mergedAirHandlerUnits,
    isSnapshotLoading,
    viewedPending,
    representativeQuery,
    isLivePaused,
  } = useStateSnapshots({
    entities: airHandlerUnits,
    mode,
    historyDate: snapshotHistoryDate,
    stateQuery: airHandlerUnitApi.queries.state,
    isViewed: ahu => groupIds.includes(ahu.id),
  });

  const airHandlerUnit = mergedAirHandlerUnits.filter(ahu => groupIds.includes(ahu.id));

  const airHandlerUnitFloors = getFloors(airHandlerUnit, floors);
  const errors = getErrors(mergedAirHandlerUnits);

  const primaryCurrent = currentAirHandlerUnit[0];
  const name = currentAirHandlerUnit.length > 1 ? primaryCurrent?.group : primaryCurrent?.name;
  if (!name) throw new Error("Group name counld't be undefined if it's a group");

  const liveDate = airHandlerUnit.reduce<string | undefined>(
    (latest, ahu) =>
      ahu.lastBmsDataReport && (!latest || ahu.lastBmsDataReport > latest) ? ahu.lastBmsDataReport : latest,
    undefined,
  );
  const liveSampleDate = liveDate ? new Date(liveDate) : undefined;
  const isLiveDataFrozen = useLiveDataFrozen(
    !isHistory,
    `${mode}:${airHandlerUnitId}`,
    representativeQuery?.dataUpdatedAt ?? 0,
    liveDate,
    REALTIME_FROZEN_FETCH_COUNT,
  );
  // Gate on `isPending` (no data yet), not `isFetching`, so background polls don't flicker the dialog closed.
  const liveStale = !viewedPending && (isLiveSampleStale(liveSampleDate) || isLiveDataFrozen);
  const isRealtimeStale = useStickyRealtimeStale(isHistory, isLivePaused, liveStale);
  const date = isHistory ? new Date(effectiveHistoryDate ?? now) : liveDate ? new Date(liveDate) : now;

  const handleReload = useCallback(() => Promise.all(stateQueries.map(query => query.refetch())), [stateQueries]);

  return (
    <AirHandlerUnitContextProvider
      name={name}
      isGroup={currentAirHandlerUnit.length > 1}
      airHandlerUnit={airHandlerUnit}
      airHandlerUnits={mergedAirHandlerUnits}
      floors={airHandlerUnitFloors}
      errors={errors}
      mode={mode}
      onModeChange={onModeChange}
      isSnapshotLoading={isSnapshotLoading}
      isHistoryDatesLoading={isHistoryDatesLoading}
      isRealtimeStale={isRealtimeStale}
      liveSampleDate={liveSampleDate}
      date={date}
      days={historyDays}
      dates={historyDates}
      onDateChange={onDateChange}
      onReload={handleReload}
    >
      {children}
    </AirHandlerUnitContextProvider>
  );
};

export { AirHandlerUnitContextProvider, AirHandlerUnitProvider, useAirHandlerUnitContext };
export type { AirHandlerUnitContextValue };
